// src/utils/security/requestInterceptor.js
// Intercepts fetch/axios requests to add fingerprinting and rate limit awareness

import { generateFingerprint, checkRateLimitHeaders, logSecurityEvent, detectAbusePattern } from './antiScraping';

/**
 * Wrap fetch to add security headers and monitor rate limits
 */
export const createSecureFetch = () => {
  const originalFetch = window.fetch;

  window.fetch = async function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    
    // Skip security checks for Razorpay API calls (payment gateway needs to work)
    const isRazorpayRequest = url.includes('razorpay.com') ||
                             url.includes('checkout.razorpay.com') ||
                             url.includes('api.razorpay.com');
    const isPublicWaitlist = url.includes('/api/waitlist/');
    
    if (!isRazorpayRequest && detectAbusePattern()) {
      logSecurityEvent('abuse_pattern_detected', {
        url: args[0],
      });
    }

    // Add fingerprint to request headers (skip Razorpay to avoid breaking checkout)
    const fingerprint = generateFingerprint();
    const options = args[1] || {};
    const headers = new Headers(options.headers || {});

    // Add fingerprint header (if backend supports it) - skip Razorpay
    if (fingerprint && !isRazorpayRequest && !isPublicWaitlist) {
      headers.set('X-Client-Fingerprint', fingerprint.hash);
      headers.set('X-Client-Timestamp', fingerprint.raw.timestamp.toString());
    }

    // Make the request
    const response = await originalFetch(args[0], {
      ...options,
      headers,
    });

    // Check rate limit headers
    const rateLimitInfo = checkRateLimitHeaders(response);
    if (rateLimitInfo) {
      // Store rate limit info for UI awareness
      try {
        sessionStorage.setItem('rate_limit_info', JSON.stringify(rateLimitInfo));
      } catch (e) {
        // Ignore storage errors
      }
    }

    return response;
  };
};

/**
 * Wrap axios to add security headers and monitor rate limits
 * Can be applied to default axios or a custom instance
 */
export const createSecureAxios = (axiosInstance) => {
  axiosInstance.defaults.withCredentials = true;

  // Add request interceptor for fingerprinting
  axiosInstance.interceptors.request.use(
    (config) => {
      config.withCredentials = true;
      const isRazorpayRequest = config.url?.includes('razorpay.com') || 
                                config.url?.includes('checkout.razorpay.com') ||
                                config.url?.includes('api.razorpay.com');
      
      const fingerprint = generateFingerprint();
      
      if (fingerprint && config.headers && !isRazorpayRequest) {
        config.headers['X-Client-Fingerprint'] = fingerprint.hash;
        config.headers['X-Client-Timestamp'] = fingerprint.raw.timestamp.toString();
      }

      // Check for abuse patterns (skip for Razorpay)
      if (!isRazorpayRequest && detectAbusePattern()) {
        logSecurityEvent('abuse_pattern_detected', {
          url: config.url,
          method: config.method,
        });
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for rate limit monitoring
  axiosInstance.interceptors.response.use(
    (response) => {
      // Check rate limit headers
      const rateLimitInfo = checkRateLimitHeaders(response);
      if (rateLimitInfo) {
        try {
          sessionStorage.setItem('rate_limit_info', JSON.stringify(rateLimitInfo));
        } catch (e) {
          // Ignore storage errors
        }
      }
      return response;
    },
    (error) => {
      // Check rate limit in error response
      if (error.response) {
        const rateLimitInfo = checkRateLimitHeaders(error.response);
        if (rateLimitInfo || error.response.status === 429) {
          logSecurityEvent('rate_limit_error', {
            status: error.response.status,
            headers: rateLimitInfo,
          });
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

/**
 * Apply security interceptors to default axios instance globally
 */
export const secureDefaultAxios = () => {
  // This will be called in index.js to secure all axios calls
  // Note: We can't modify the default axios export, but we can add interceptors
  // The interceptors will apply to all axios instances created from the default
  try {
    const axios = require('axios').default;
    return createSecureAxios(axios);
  } catch (e) {
    // If axios is not available, return null
    return null;
  }
};

/**
 * Initialize request interceptors
 */
export const initializeRequestInterceptors = () => {
  if (typeof window !== 'undefined') {
    createSecureFetch();
  }
};
